'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { api } from '@/lib/api';
import { getDevice, getReferrer, getSessionId, getUiLang } from '@/lib/analytics';

/**
 * 全站页面访问埋点（v2）：路由变化时上报一次（1.5s 内同路径去重），
 * 携带用户 JWT（登录时）或匿名 sessionId（未登录时），
 * 并上报 referrer（跨站来源）/ device / lang；
 * 页面离开时补报停留时长（duration），供后台「访问统计 / Analytics」
 * 按日 × 页面聚合 PV / UV、行为与兴趣多维分析。
 */
export default function PageViewTracker() {
  const pathname = usePathname();
  const lastRef = useRef<{ path: string; ts: number; enteredAt: number }>({
    path: '',
    ts: 0,
    enteredAt: 0,
  });

  useEffect(() => {
    if (!pathname) return;
    // 后台管理页面不计入前台访问统计
    if (pathname.startsWith('/portal-') || pathname === '/admin' || pathname.startsWith('/admin')) return;
    const now = Date.now();
    const last = lastRef.current;
    const sessionId = getSessionId();

    // 路由切换：先补报上一页停留时长（≥1s 才记，避免 0 秒噪音）
    if (last.path && last.path !== pathname && last.enteredAt > 0 && now - last.enteredAt >= 1000) {
      void api
        .post('/track/duration', {
          path: last.path,
          sessionId,
          durationMs: Math.min(7_200_000, now - last.enteredAt),
        })
        .catch(() => {
          /* fire-and-forget */
        });
    }

    if (last.path === pathname && now - last.ts < 1500) return;
    lastRef.current = {
      path: pathname,
      ts: now,
      enteredAt: last.path === pathname ? last.enteredAt : now,
    };

    const pageName =
      typeof document !== 'undefined' ? (document.title || '').slice(0, 120) : '';
    void api
      .post('/track/page-view', {
        path: pathname,
        pageName,
        sessionId,
        referrer: getReferrer(),
        device: getDevice(),
        lang: getUiLang(),
      })
      .catch(() => {
        /* fire-and-forget: analytics must never break navigation */
      });
  }, [pathname]);

  // 离开页面（刷新/关闭/切后台）时补报当前页停留时长
  useEffect(() => {
    if (!pathname) return;
    const sessionId = getSessionId();
    const onLeave = () => {
      const last = lastRef.current;
      if (!last.path || last.enteredAt <= 0) return;
      const dur = Date.now() - last.enteredAt;
      if (dur >= 1000) {
        void api
          .post('/track/duration', { path: last.path, sessionId, durationMs: Math.min(7_200_000, dur) })
          .catch(() => {
            /* fire-and-forget */
          });
      }
    };
    const h = () => onLeave();
    const v = () => {
      if (document.visibilityState === 'hidden') onLeave();
    };
    window.addEventListener('pagehide', h);
    document.addEventListener('visibilitychange', v);
    return () => {
      window.removeEventListener('pagehide', h);
      document.removeEventListener('visibilitychange', v);
    };
  }, [pathname]);

  return null;
}
